import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { USER_API } from "config";
import { useAxios } from "hooks/useAxios";
import { useStepUpProtectedAction } from "hooks/auth";
import { workspaceKeys } from "queries";
import { handleToast } from "utils";
import { API_KEY_SCOPE_VALUES, ApiKeyScopes, type ApiKeyScopeValue } from "utils/apiKeyScopes";

export { API_KEY_SCOPE_VALUES, type ApiKeyScopeValue };

export const API_KEY_SCOPE_GROUPS: Array<{
  label: string;
  options: Array<{ value: ApiKeyScopeValue; label: string }>;
}> = [
  {
    label: "Projects",
    options: [
      { value: ApiKeyScopes.PROJECTS_READ, label: "Read projects & resources" },
      { value: ApiKeyScopes.PROJECTS_CREATE, label: "Create projects" },
      { value: ApiKeyScopes.PROJECTS_UPDATE, label: "Update project settings & metadata" },
      { value: ApiKeyScopes.PROJECTS_ARCHIVE, label: "Archive & unarchive projects" },
      { value: ApiKeyScopes.PROJECTS_DELETE, label: "Delete projects" },
      { value: ApiKeyScopes.PROJECTS_EXPORT, label: "Export reports & files" },
    ],
  },
  {
    label: "Screenplay",
    options: [
      { value: ApiKeyScopes.SCREENPLAY_UPLOAD, label: "Script — upload versions" },
      { value: ApiKeyScopes.SCREENPLAY_EDIT, label: "Script — edit content & scenes" },
      { value: ApiKeyScopes.SCREENPLAY_LOCK, label: "Script — lock & unlock" },
      { value: ApiKeyScopes.SCREENPLAY_SHARE_LINKS, label: "Script — manage share links" },
      { value: ApiKeyScopes.SCREENPLAY_BREAKDOWN, label: "Tags — edit script tags" },
      { value: ApiKeyScopes.BREAKDOWN_RERUN, label: "Tags — run script breakdown AI" },
    ],
  },
  {
    label: "Characters",
    options: [
      { value: ApiKeyScopes.CHARACTERS_READ, label: "Characters — view" },
      { value: ApiKeyScopes.CHARACTERS_EDIT, label: "Characters — edit profiles" },
      { value: ApiKeyScopes.CHARACTERS_RERUN, label: "Characters — run breakdown AI" },
      { value: ApiKeyScopes.ACTORS_READ, label: "Actors — view casting" },
      { value: ApiKeyScopes.ACTORS_EDIT, label: "Actors — edit & assign" },
    ],
  },
  {
    label: "Scheduling",
    options: [
      { value: ApiKeyScopes.SCHEDULING_READ, label: "View schedules" },
      { value: ApiKeyScopes.SCHEDULING_EDIT, label: "Edit stripboard, locations & daybreaks" },
      { value: ApiKeyScopes.SCHEDULING_RERUN, label: "Run scheduling solver" },
    ],
  },
  {
    label: "Budgeting",
    options: [
      { value: ApiKeyScopes.BUDGETING_READ, label: "View budgets" },
      { value: ApiKeyScopes.BUDGETING_EDIT, label: "Edit budget lines & versions" },
      { value: ApiKeyScopes.BUDGETING_RERUN, label: "Run budget generation" },
    ],
  },
  {
    label: "Coverage",
    options: [
      { value: ApiKeyScopes.COVERAGE_READ, label: "View coverage" },
      { value: ApiKeyScopes.COVERAGE_EDIT, label: "Edit coverage" },
      { value: ApiKeyScopes.COVERAGE_RERUN, label: "Run coverage AI" },
    ],
  },
  {
    label: "Workspace",
    options: [
      { value: ApiKeyScopes.WORKSPACE_READ, label: "View workspace details" },
      { value: ApiKeyScopes.WORKSPACE_MEMBERS_READ, label: "List workspace members" },
      { value: ApiKeyScopes.WORKSPACE_ACTIVITY_READ, label: "View activity logs" },
    ],
  },
];

export const API_KEY_SCOPE_OPTIONS = API_KEY_SCOPE_GROUPS.flatMap((group) => group.options);

export type ApiKeyRecord = {
  key_id: string;
  name: string;
  token_prefix: string;
  token_suffix: string | null;
  scopes: ApiKeyScopeValue[];
  workspace_id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  created_by_email?: string;
  /** Why requests with this key are refused, e.g. "account_inactive"; null when it works. */
  inactive_reason?: string | null;
};

export type ApiKeyWithToken = ApiKeyRecord & {
  token: string;
};

type CreateApiKeyInput = {
  name: string;
  scopes: ApiKeyScopeValue[];
  never_expires?: boolean;
  expires_in_days?: number;
};

export const useApiKeys = (workspaceId: string | undefined, view: "mine" | "all" = "mine") => {
  const axios = useAxios({ allResponse: true });
  const queryClient = useQueryClient();
  const { runProtectedAction } = useStepUpProtectedAction();
  const enabled = Boolean(workspaceId);

  const keysQuery = useQuery<ApiKeyRecord[]>({
    queryKey: workspaceKeys.apiKeys(workspaceId ?? "", view),
    queryFn: async () => {
      const response = await axios.get<{ data: ApiKeyRecord[] }>(
        USER_API.listWorkspaceApiKeys(workspaceId!, view)
      );
      return response.data.data;
    },
    enabled,
    refetchOnWindowFocus: false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: workspaceKeys.apiKeys(workspaceId ?? "", view),
    });
    if (view === "mine") {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.apiKeys(workspaceId ?? "", "all"),
      });
    }
  };

  const createKeyMutation = useMutation({
    mutationFn: async (input: CreateApiKeyInput) =>
      runProtectedAction(async () => {
        const response = await axios.post(USER_API.createWorkspaceApiKey(workspaceId!), input);
        return response.data;
      }),
    onSuccess: () => {
      invalidate();
      handleToast("API key created.", "success");
    },
    onError: () => {
      handleToast("Failed to create API key.", "error");
    },
  });

  const rotateKeyMutation = useMutation({
    mutationFn: async (keyId: string) =>
      runProtectedAction(async () => {
        const response = await axios.post(USER_API.rotateWorkspaceApiKey(workspaceId!, keyId));
        return response.data;
      }),
    onSuccess: () => {
      invalidate();
      handleToast("API key rotated.", "success");
    },
    onError: () => {
      handleToast("Failed to rotate API key.", "error");
    },
  });

  const renameKeyMutation = useMutation({
    mutationFn: async ({ keyId, name }: { keyId: string; name: string }) => {
      const response = await axios.patch(USER_API.renameWorkspaceApiKey(workspaceId!, keyId), {
        name,
      });
      return response.data;
    },
    onSuccess: () => {
      invalidate();
      handleToast("API key renamed.", "success");
    },
    onError: () => {
      handleToast("Failed to rename API key.", "error");
    },
  });

  const revokeKeyMutation = useMutation({
    mutationFn: async (keyId: string) =>
      runProtectedAction(async () => {
        await axios.delete(USER_API.revokeWorkspaceApiKey(workspaceId!, keyId));
      }),
    onSuccess: () => {
      invalidate();
      handleToast("API key revoked.", "success");
    },
    onError: () => {
      handleToast("Failed to revoke API key.", "error");
    },
  });

  return {
    keysQuery,
    createKeyMutation,
    rotateKeyMutation,
    renameKeyMutation,
    revokeKeyMutation,
    isCreating: createKeyMutation.isPending,
    isRotating: rotateKeyMutation.isPending,
    isRenaming: renameKeyMutation.isPending,
    isRevoking: revokeKeyMutation.isPending,
  };
};
